import { generateId } from "../../utils/generateId";
import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { RootState } from "../rootReducer";
import { SearchHistory } from "../../models/search-history";
import { makeSyncUpsert } from "../syncUpsert";

export const searchHistoryAdapter = createEntityAdapter<SearchHistory>();
type SearchHistoryState = EntityState<SearchHistory, string>;
const initialState: SearchHistoryState = searchHistoryAdapter.getInitialState();

type AddSearchPayload = {
  query: string;
  resultCount: number;
};

export const searchHistorySlice = createSlice({
  name: "searchHistory",
  initialState,
  reducers: {
    upsertManyFromSync: makeSyncUpsert(searchHistoryAdapter),
    addSearchHistory(state: SearchHistoryState, action: PayloadAction<AddSearchPayload>) {
      const query = action.payload.query.trim();
      if (!query) return;
      const now = new Date().toISOString();

      // Dedup by query: bump the existing entry instead of storing a duplicate.
      const existing = Object.values(state.entities).find((sh) => sh?.query === query);

      if (existing) {
        searchHistoryAdapter.updateOne(state, {
          id: existing.id,
          changes: {
            searchedAt: now,
            resultCount: action.payload.resultCount,
            deletedAt: undefined, // resurrect if it had been cleared
          },
        });
      } else {
        searchHistoryAdapter.addOne(state, {
          id: generateId(),
          query,
          resultCount: action.payload.resultCount,
          searchedAt: now,
          createdAt: now,
          updatedAt: now,
        });
      }
    },
    removeSearchHistory(state: SearchHistoryState, action: PayloadAction<string>) {
      const existing = state.entities[action.payload];
      if (existing) {
        const now = new Date().toISOString();
        searchHistoryAdapter.updateOne(state, {
          id: action.payload,
          changes: { deletedAt: now },
        });
      }
    },
    clearSearchHistory(state: SearchHistoryState) {
      // Soft-delete so the clear propagates via sync.
      const now = new Date().toISOString();
      const updates = Object.values(state.entities)
        .filter((sh) => sh && !sh.deletedAt)
        .map((sh) => ({ id: sh!.id, changes: { deletedAt: now } }));
      searchHistoryAdapter.updateMany(state, updates);
    },
  },
});

// selectAll* hides soft-deleted rows; sync reads entities directly.
export const selectAllSearchHistory = createSelector(
  searchHistoryAdapter.getSelectors((state: RootState) => state.searchHistory).selectAll,
  (items) => items.filter((sh) => !sh.deletedAt),
);

// Recent searches, newest first, optionally limited.
export const selectRecentSearches = (limit?: number) =>
  createSelector(selectAllSearchHistory, (items) => {
    const sorted = [...items].sort((a, b) => b.searchedAt.localeCompare(a.searchedAt));
    return limit ? sorted.slice(0, limit) : sorted;
  });

export const {
  addSearchHistory,
  removeSearchHistory,
  clearSearchHistory,
  upsertManyFromSync: upsertSearchHistoryFromSync,
} = searchHistorySlice.actions;

export default searchHistorySlice.reducer;
